import { EditorView } from '@codemirror/view'
import { HighlightStyle, syntaxHighlighting } from '@codemirror/language'
import { tags as t } from '@lezer/highlight'
import { Extension } from '@codemirror/state'

const colors = {
  bg:       'var(--bg-2)',
  bgLine:   'var(--bg-3)',
  gutterBg: 'var(--bg-2)',
  gutterFg: 'var(--text-2)',
  gutterActive: 'var(--text-0)',
  selection: 'rgba(124,110,245,0.2)',
  cursor:   'var(--accent)',
  text:     'var(--text-0)',
  comment:  'var(--text-2)',
  keyword:  '#c07ef5',
  fn:       '#6ab0f5',
  string:   'var(--accent-2)',
  number:   'var(--accent-3)',
  type:     '#f5d76e',
  operator: 'var(--text-1)',
  variable: 'var(--text-0)',
  property: '#6ab0f5',
  tag:      'var(--red)',
  attr:     'var(--accent-3)',
  meta:     'var(--text-1)',
}

// Base editor theme
const baseTheme = EditorView.theme({
  '&': {
    color: colors.text,
    backgroundColor: colors.bg,
    height: '100%',
    fontSize: '13px',
    fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
  },
  '.cm-content': {
    caretColor: colors.cursor,
    padding: '16px 0',
    lineHeight: '1.7',
  },
  '.cm-cursor, .cm-dropCursor': {
    borderLeftColor: colors.cursor,
    borderLeftWidth: '2px',
  },
  '&.cm-focused .cm-cursor': { borderLeftColor: colors.cursor },
  '&.cm-focused .cm-selectionBackground, .cm-selectionBackground, .cm-content ::selection': {
    backgroundColor: colors.selection,
  },
  '.cm-panels': { backgroundColor: 'var(--bg-1)', color: 'var(--text-0)' },
  '.cm-panels.cm-panels-top': { borderBottom: '1px solid var(--border)' },
  '.cm-panels.cm-panels-bottom': { borderTop: '1px solid var(--border)' },
  '.cm-searchMatch': { backgroundColor: '#f5a15730', outline: '1px solid #f5a15760' },
  '.cm-searchMatch.cm-searchMatch-selected': { backgroundColor: '#f5a15760' },
  '.cm-activeLine': { backgroundColor: colors.bgLine },
  '.cm-selectionMatch': { backgroundColor: '#7c6ef520' },
  '&.cm-focused .cm-matchingBracket, &.cm-focused .cm-nonmatchingBracket': {
    backgroundColor: '#7c6ef540',
  },
  '.cm-gutters': {
    backgroundColor: colors.gutterBg,
    color: colors.gutterFg,
    border: 'none',
    paddingRight: '4px',
    minWidth: '44px',
    borderRight: '1px solid var(--border)',
  },
  '.cm-activeLineGutter': {
    backgroundColor: 'transparent',
    color: colors.gutterActive,
  },
  '.cm-lineNumbers .cm-gutterElement': {
    padding: '0 8px 0 4px',
    minWidth: '32px',
    textAlign: 'right',
  },
  '.cm-foldPlaceholder': {
    backgroundColor: 'var(--bg-3)',
    border: '1px solid var(--text-2)',
    color: colors.text,
    borderRadius: '4px',
    padding: '0 6px',
  },
  '.cm-tooltip': {
    border: '1px solid var(--border)',
    backgroundColor: 'var(--bg-1)',
    borderRadius: '8px',
  },
  '.cm-tooltip .cm-tooltip-arrow:before': { borderTopColor: 'var(--border)' },
  '.cm-tooltip .cm-tooltip-arrow:after': { borderTopColor: 'var(--bg-1)' },
  '.cm-tooltip-autocomplete': {
    '& > ul > li[aria-selected]': {
      backgroundColor: '#7c6ef520',
      color: colors.text,
    },
  },
  '.cm-scroller': { overflow: 'auto' },
}, { dark: true })

// Syntax highlight style
const pulseHighlight = HighlightStyle.define([
  { tag: t.keyword,               color: colors.keyword,   fontStyle: 'italic' },
  { tag: [t.name, t.deleted, t.character, t.propertyName, t.macroName],
                                  color: colors.variable },
  { tag: [t.function(t.variableName), t.function(t.propertyName), t.labelName],
                                  color: colors.fn },
  { tag: [t.color, t.constant(t.name), t.standard(t.name)],
                                  color: colors.number },
  { tag: [t.definition(t.name), t.separator],
                                  color: colors.text },
  { tag: [t.typeName, t.className, t.number, t.changed, t.annotation, t.modifier, t.self, t.namespace],
                                  color: colors.type },
  { tag: [t.operator, t.operatorKeyword, t.url, t.escape, t.regexp, t.link, t.special(t.string)],
                                  color: colors.operator },
  { tag: [t.meta, t.comment],     color: colors.comment,   fontStyle: 'italic' },
  { tag: t.strong,                fontWeight: 'bold' },
  { tag: t.emphasis,              fontStyle: 'italic' },
  { tag: t.strikethrough,         textDecoration: 'line-through' },
  { tag: t.link,                  color: colors.string,    textDecoration: 'underline' },
  { tag: t.heading,               color: colors.keyword,   fontWeight: 'bold' },
  { tag: [t.atom, t.bool, t.special(t.variableName)],
                                  color: colors.number },
  { tag: [t.processingInstruction, t.string, t.inserted],
                                  color: colors.string },
  { tag: t.invalid,               color: colors.tag },
])

export const pulseTheme: Extension = [
  baseTheme,
  syntaxHighlighting(pulseHighlight),
]
